// Shared deck-generation option tables.
//
// Used by the workflow Collaterals node config panel and the Materials page
// "Generate with AI" modal — one source of truth so the two surfaces can
// never drift apart on values the backend accepts.
import type { DeckContentFocus, DeckColorTheme, EmailTone } from '@/types/workflow'

export const contentFocusOptions: { value: DeckContentFocus; label: string; description: string }[] = [
  { value: 'pitch_deck', label: 'Pitch Deck', description: 'Problem -> Solution -> Benefits -> CTA' },
  { value: 'product_overview', label: 'Product Overview', description: 'Features, tech advantages, integration' },
  { value: 'case_study', label: 'Case Study', description: 'Challenge -> Solution -> Results' },
  { value: 'industry_analysis', label: 'Industry Analysis', description: 'Market trends, challenges, recommendations' },
  { value: 'custom', label: 'Custom', description: 'Follow your custom instructions' },
]

export const deckToneOptions: { value: EmailTone; label: string }[] = [
  { value: 'professional', label: 'Professional' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'casual', label: 'Casual' },
  { value: 'persuasive', label: 'Persuasive' },
  { value: 'urgent', label: 'Urgent' },
]

// Each preset is monochromatic -- accent is a brighter shade of the SAME
// hue as primary, matching the backend's PRESET_THEMES (generator.py),
// not a contrasting color picked for "pop".
export const deckColorThemes: { value: DeckColorTheme; label: string; primary: string; accent: string; description: string }[] = [
  { value: 'corporate_blue', label: 'Corporate Blue', primary: '#1E40AF', accent: '#3B82F6', description: 'Clean and professional' },
  { value: 'dark_mode', label: 'Dark Mode', primary: '#1F2937', accent: '#60A5FA', description: 'Modern and bold' },
  { value: 'warm_sunset', label: 'Warm Sunset', primary: '#9A3412', accent: '#F97316', description: 'Warm and inviting' },
  { value: 'forest_green', label: 'Forest Green', primary: '#166534', accent: '#22C55E', description: 'Natural and trustworthy' },
  { value: 'royal_purple', label: 'Royal Purple', primary: '#6B21A8', accent: '#A855F7', description: 'Creative and premium' },
  { value: 'ocean_teal', label: 'Ocean Teal', primary: '#0F766E', accent: '#14B8A6', description: 'Calm and confident' },
  { value: 'slate_minimal', label: 'Slate Minimal', primary: '#334155', accent: '#3B82F6', description: 'Minimal and elegant' },
  { value: 'auto_from_brand', label: 'Auto from Brand', primary: '#6366f1', accent: '#818cf8', description: 'AI picks a monochromatic palette from brand context' },
  // Was a valid DeckColorTheme with a whole custom-color-picker UI below
  // (config.color_theme === 'custom') that no swatch ever selected — pick
  // your own colors was unreachable (WI 21908).
  { value: 'custom', label: 'Custom', primary: '#1E40AF', accent: '#3B82F6', description: 'Pick your own colors' },
]

// Content types the deck pipeline can use as source material (websites are
// deliberately excluded — same rule as the Collaterals node).
export const DECK_SOURCE_CONTENT_TYPES = new Set([
  'pdf', 'docx', 'pptx', 'excel', 'csv', 'text', 'markdown',
])

// Pre-defined audience roles (designations) for the Generate-with-AI modal.
// The selected value is fed verbatim into the deck-generation prompt as the
// audience the content should speak to. '' = no specific targeting.
export const deckTargetRoleOptions: { value: string; label: string }[] = [
  { value: '', label: 'Any audience' },
  { value: 'Executive Leadership (CEO / Founders)', label: 'Executive Leadership (CEO / Founders)' },
  { value: 'Engineering Leadership (CTO / VP Engineering)', label: 'Engineering Leadership (CTO / VP Engineering)' },
  { value: 'Software Engineers', label: 'Software Engineers' },
  { value: 'IT & Infrastructure', label: 'IT & Infrastructure' },
  { value: 'Data & Analytics', label: 'Data & Analytics' },
  { value: 'Product Management', label: 'Product Management' },
  { value: 'Marketing', label: 'Marketing' },
  { value: 'Sales', label: 'Sales' },
  { value: 'Customer Success & Support', label: 'Customer Success & Support' },
  { value: 'Human Resources', label: 'Human Resources' },
  { value: 'Finance & Accounting', label: 'Finance & Accounting' },
  { value: 'Operations & Supply Chain', label: 'Operations & Supply Chain' },
  { value: 'Procurement', label: 'Procurement' },
  { value: 'Legal & Compliance', label: 'Legal & Compliance' },
]

// Pre-defined target industries — same contract as the roles above.
export const deckTargetIndustryOptions: { value: string; label: string }[] = [
  { value: '', label: 'Any industry' },
  { value: 'Technology & Software', label: 'Technology & Software' },
  { value: 'Financial Services & Banking', label: 'Financial Services & Banking' },
  { value: 'Insurance', label: 'Insurance' },
  { value: 'Healthcare & Life Sciences', label: 'Healthcare & Life Sciences' },
  { value: 'Pharmaceuticals & Biotech', label: 'Pharmaceuticals & Biotech' },
  { value: 'Retail & E-commerce', label: 'Retail & E-commerce' },
  { value: 'Manufacturing', label: 'Manufacturing' },
  { value: 'Logistics & Supply Chain', label: 'Logistics & Supply Chain' },
  { value: 'Real Estate & Construction', label: 'Real Estate & Construction' },
  { value: 'Energy & Utilities', label: 'Energy & Utilities' },
  { value: 'Telecommunications', label: 'Telecommunications' },
  { value: 'Media & Entertainment', label: 'Media & Entertainment' },
  { value: 'Hospitality & Travel', label: 'Hospitality & Travel' },
  { value: 'Automotive', label: 'Automotive' },
  { value: 'Agriculture & Food', label: 'Agriculture & Food' },
  { value: 'Education', label: 'Education' },
  { value: 'Government & Public Sector', label: 'Government & Public Sector' },
  { value: 'Professional Services & Consulting', label: 'Professional Services & Consulting' },
  { value: 'Nonprofit', label: 'Nonprofit' },
]
